import React, { useEffect, useState } from "react";
import { BadgePercent, Gift, Layers, Package, Search, ShoppingBag, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";

import Cabecalho from "../comum/Cabecalho";
import Carregando from "../comum/Carregando";
import FiltroPeriodo from "../comum/FiltroPeriodo";
import GraficoDias from "../comum/GraficoDias";
import Kpi from "../comum/Kpi";
import SemanaHora from "../comum/SemanaHora";
import Seletor from "../comum/Seletor";
import { num, pct, qtdTotal, qtdVendida, reais } from "../comum/formato";
import { useFiltros } from "../comum/useFiltros";
import { ROTA_HUB_OFERTAS } from "../rotas";
import { carregarPromocoes } from "./api";
import Avisos from "./Avisos";
import ProdutosPromocao from "./ProdutosPromocao";
import ResumoFrase from "./ResumoFrase";
import TabelaPromocoes from "./TabelaPromocoes";

const STATUS = [{ id: "ativa", rotulo: "Ativa" }, { id: "desativada", rotulo: "Desativada" }, { id: "agendada", rotulo: "Agendada" },
  { id: "encerrada", rotulo: "Encerrada" }, { id: "excluida", rotulo: "Excluída" }];

// Relatórios → Relatório de Ofertas → Promoções.
export default function RelatorioPromocoesPage() {
  const { filtros, mudar, query, paraApi } = useFiltros();
  const [rel, setRel] = useState(null);
  // Erro ao carregar: aviso com "Tentar de novo" no lugar do carregamento sem fim.
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  const [busca, setBusca] = useState(filtros.busca || "");
  const chave = JSON.stringify(paraApi);

  useEffect(() => {
    let vivo = true;
    // Trocar período/filtro mantém o relatório na tela até o novo chegar (25/10/2026)
    setErro(false);
    carregarPromocoes(paraApi).then((d) => { if (vivo) setRel(d); })
      .catch(() => { if (vivo) { setErro(true); toast.error("Não foi possível carregar o relatório de promoções"); } });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, tentativa]);
  useEffect(() => {
    const t = setTimeout(() => { if ((filtros.busca || "") !== busca) mudar({ busca }); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca]);

  const r = rel?.resumo || {};
  const a = rel?.anterior || {};
  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-5" data-e2e="relatorio-promocoes">
        <Cabecalho voltarPara={ROTA_HUB_OFERTAS} voltarRotulo="Relatório de Ofertas" icone={Gift} corIcone="bg-emerald-500/10 text-emerald-500"
          titulo="Relatório de Promoções" subtitulo="Quanto cada promoção vendeu, quanto descontou e quanto sobrou de lucro pela ficha técnica." />

        <div className="flex flex-wrap items-center gap-2">
          <FiltroPeriodo de={filtros.de} ate={filtros.ate} onMudar={mudar} cor="bg-emerald-500 text-white" />
          <Seletor rotulo="Módulo" valor={filtros.modulo} opcoes={rel?.opcoes?.modulos} onMudar={(v) => mudar({ modulo: v })} e2e="filtro-modulo" />
          <Seletor rotulo="Tipo" valor={filtros.tipo} opcoes={rel?.opcoes?.tipos} onMudar={(v) => mudar({ tipo: v })} e2e="filtro-tipo" />
          <Seletor rotulo="Status" valor={filtros.status} opcoes={STATUS} onMudar={(v) => mudar({ status: v })} />
          <label className="flex items-center gap-1.5 rounded-lg border bg-card px-2.5 py-1 text-xs">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome da promoção" className="bg-transparent outline-none w-36" data-e2e="busca-promocao" />
          </label>
        </div>

        {!rel ? <Carregando erro={erro} onTentar={() => setTentativa((t) => t + 1)} /> : (
          <>
            <ResumoFrase periodo={rel.periodo} r={r} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi rotulo="Vendidos em promoção" valor={qtdVendida(r)} atual={qtdTotal(r)} anterior={qtdTotal(a)} dica={`${r.pedidos} pedidos`} icone={Package} e2e="kpi-unidades" />
              <Kpi rotulo="Faturado" valor={reais(r.faturado)} atual={r.faturado} anterior={a.faturado} icone={Wallet} destaque="bg-emerald-500/10 border-emerald-500/30" e2e="kpi-faturado" />
              <Kpi rotulo="Desconto concedido" valor={reais(r.desconto)} atual={r.desconto} anterior={a.desconto} menorMelhor icone={BadgePercent} e2e="kpi-desconto" />
              <Kpi rotulo="Lucro (ficha técnica)" valor={r.lucro == null ? "—" : reais(r.lucro)} atual={r.lucro} anterior={a.lucro} dica={r.lucro == null ? "sem ficha técnica" : `${pct(r.margem_pct)} margem`} icone={TrendingUp} e2e="kpi-lucro" />
              <Kpi rotulo="Custo dos produtos" valor={r.custo == null ? "—" : reais(r.custo)} atual={r.custo} anterior={a.custo} menorMelhor dica={`ficha em ${pct(r.cobertura_custo_pct)}`} icone={Layers} />
              <Kpi rotulo="Retorno do desconto" valor={r.retorno ? reais(r.retorno) : "—"} atual={r.retorno} anterior={a.retorno} dica="em vendas por R$ 1" icone={TrendingUp} />
              <Kpi rotulo="Levaram junto" valor={reais(r.vendas_acompanhantes)} atual={r.vendas_acompanhantes} anterior={a.vendas_acompanhantes} dica="em outros produtos" icone={ShoppingBag} />
              <Kpi rotulo="Ticket dos pedidos" valor={reais(r.ticket_pedidos)} atual={r.ticket_pedidos} anterior={a.ticket_pedidos} dica={`${r.clientes} clientes`} icone={Wallet} />
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">
              Setas: comparação com os {rel.periodo.dias} dias anteriores. Pedidos cancelados, iFood e Campanha Inteligente ficam de fora.
            </p>
            <Avisos resumo={r} abaixoDoCusto={rel.abaixo_do_custo} />
            <div className="grid lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3">
                <GraficoDias serie={rel.por_dia} principal="faturado" secundaria="desconto" titulo="Vendas em promoção por dia" corPrincipal="bg-emerald-500"
                  legenda={[{ rotulo: "Faturado", cor: "bg-emerald-500" }, { rotulo: "Desconto", cor: "bg-amber-400" }]} />
              </div>
              <div className="lg:col-span-2"><SemanaHora semana={rel.semana} hora={rel.hora} titulo="Quando as promoções vendem" /></div>
            </div>
            <TabelaPromocoes promocoes={rel.promocoes} query={query()} />
            <ProdutosPromocao produtos={rel.produtos} />
          </>
        )}
      </div>
    </div>
  );
}
